/**
 * Register lookup — `server/app/routers/registers.py`.
 *
 * A number in, a case out, over the job queue. Three calls:
 *
 * - `useCreateLookup` POSTs the number. The answer is either the queued lookup
 *   or — for a number like `1886984`, which is a valid US registration, a
 *   valid WIPO international registration AND a valid UK application — the
 *   candidates for the person to choose between. **Ambiguity is not an
 *   error**: the server answers 201 with `resolved: false`, and this module
 *   never collapses the candidates into a guess.
 * - `useLookup` polls. `status` is the request's progress (`queued → running →
 *   done | failed`); whether a case exists is `record.found`, because "the
 *   queue is healthy" and "there is a case" are different facts. Polling stops
 *   by itself once the lookup reaches either terminal state.
 * - `useLandLookup` starts a matter from a finished lookup — biblio as
 *   prefills, the register's documents attached, per-class goods recorded —
 *   and hands back the refusal's text for the wizard.
 *
 * The record's `gaps` are rendered, never filtered: "UKIPO publishes no
 * examination reports" and "EUIPO lists documents it will not hand over" are
 * the difference between an empty list and an office that has nothing to give,
 * and hiding them would turn honest absences back into silent zeros.
 */

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'

import { api } from './client'

// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------

export interface LookupCandidate {
  office: string
  office_name: string
  number: string
  kind: string
  /** Why this reading is open — shown beside the choice. */
  why: string
}

export interface CaseDocumentOut {
  date: string
  kind: string
  description: string
  url: string
  blob_id: string
  size_bytes: number
  /** False when the register lists the document but publishes no way to get
   * it — EUIPO's access model, not a download failure. */
  obtainable: boolean
  needs_ocr: boolean | null
  chars: number | null
}

/** The subset of the stored CaseRecord the screens read. */
export interface CaseRecordOut {
  found: boolean
  office: string
  number: string
  mark: string
  owner: string
  representative: string
  status: string
  status_date: string
  filed: string
  registered: string
  expires: string
  classes: string[]
  goods: string
  goods_by_class: Record<string, string>
  ir_number: string
  documents: CaseDocumentOut[]
  /** Dated things that happened to the file — the USPTO's prosecution history,
   * the other registers' timelines. Optional so a stored record from before it
   * was shown still types. */
  events?: { date: string; what: string; detail?: string; kind?: string }[]
  deadlines: { date: string; what: string }[]
  image_url: string
  source: string
  source_urls: string[]
  gaps: string[]
}

export interface LookupOut {
  id: string
  office: string
  office_name: string
  number: string
  kind: string
  raw_input: string
  status: 'queued' | 'running' | 'done' | 'failed'
  record: CaseRecordOut | null
  error: string | null
  matter_id: string | null
  created_at: string
  finished_at: string | null
}

export interface LookupResolution {
  resolved: boolean
  candidates: LookupCandidate[]
  note: string
  lookup: LookupOut | null
}

export interface LandedOut {
  matter_id: string
  /** The refusal's extracted text, ready for the wizard. `null` when no
   * refusal was published or the stored one is a scan. */
  document_text: string | null
  needs_ocr: boolean
  /** Which stored document supplied `document_text`. A Madrid registration is
   * refused by each designated office separately and WIPO files them all
   * against the same number, so "the refusal" is a choice — naming it is what
   * lets an attorney see, and correct, which one was read. */
  document_blob_id: string | null
  /** The digest of that document — the key `GET /api/matters/{id}/blobs/{sha}`
   * serves it under, so the confirm step can offer to open the PDF itself. */
  document_sha256: string | null
  /** True when the document that was read does not name the office chosen.
   * Kept as a flag rather than left for the screen to grep out of `notes`: a
   * condition that changes what a person should do has to survive a rewording. */
  document_office_mismatch: boolean
  /** The holder's name as the matter stored it — WIPO's own field carries the
   * postal address too, and this box is a party, not an address. */
  applicant_name: string | null
  notes: string[]
}

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

export const registerKeys = {
  lookup: (id: string) => ['registers', 'lookups', id] as const,
}

export function useCreateLookup() {
  return useMutation<LookupResolution, unknown, { number: string; office?: string }>({
    mutationFn: (body) => api.post<LookupResolution>('/api/registers/lookups', body),
  })
}

/** How often a live lookup is re-read. A register answer takes seconds, not
 * milliseconds, and the queue itself is the thing doing the waiting. */
const LOOKUP_POLL_MS = 2_000

export function useLookup(lookupId: string | null): UseQueryResult<LookupOut> {
  return useQuery<LookupOut>({
    queryKey: registerKeys.lookup(lookupId ?? ''),
    queryFn: () =>
      api.get<LookupOut>(`/api/registers/lookups/${encodeURIComponent(lookupId ?? '')}`),
    enabled: Boolean(lookupId),
    // Stops by itself at done/failed: polling a terminal row forever is the
    // dead-run spin `usePipelineRun` already learned to avoid.
    refetchInterval: (query) => {
      const status = query.state.data?.status
      return status === 'queued' || status === 'running' ? LOOKUP_POLL_MS : false
    },
  })
}

export function useLandLookup() {
  const client = useQueryClient()
  return useMutation<
    LandedOut,
    unknown,
    { lookupId: string; office?: string; documentBlobId?: string }
  >({
    mutationFn: ({ lookupId, office, documentBlobId }) =>
      api.post<LandedOut>(`/api/registers/lookups/${encodeURIComponent(lookupId)}/matter`, {
        ...(office ? { office } : {}),
        ...(documentBlobId ? { document_blob_id: documentBlobId } : {}),
      }),
    onSuccess: (_landed, { lookupId }) => {
      // The lookup row now carries its matter_id; the next read shows it.
      void client.invalidateQueries({ queryKey: registerKeys.lookup(lookupId) })
    },
  })
}
